// FORKED admin sidebar — independent from src/components/employee/**.
import { useState } from 'react';
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { BarChart3, Activity, Lightbulb, Shield, LogOut, Loader2, ShieldAlert, FileText, Users, Menu, Building2 } from 'lucide-react';
import { RoleSwitcher } from '@/components/layout/RoleSwitcher';
import { AdminSessionBanner } from '@/components/layout/AdminSessionBanner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminGuard } from '@/lib/admin/useAdminGuard';
import { usePlatformAdmin } from '@/lib/platform/usePlatformAdmin';

const nav = [
  { to: '/app/admin', label: 'Overview', icon: BarChart3, end: true },
  { to: '/app/admin/people', label: 'People', icon: Users },
  { to: '/app/admin/trends', label: 'Trends', icon: Activity },
  { to: '/app/admin/surveys', label: 'Surveys', icon: FileText },
  { to: '/app/admin/insights', label: 'Insights', icon: Lightbulb },
  { to: '/app/admin/trust', label: 'Trust', icon: Shield },
];

// Gauntlet is platform-admin only — hidden from tenant admins entirely.
const platformOnlyNav = [
  { to: '/app/admin/gauntlet', label: 'Gauntlet', icon: ShieldAlert },
];

// Resolve a friendly section label for the current admin route. Deep survey
// routes (editor / report / analytics) all collapse to "Surveys" so the mobile
// top bar stays uncluttered at 390px.
function useAdminSectionLabel(): string {
  const { pathname } = useLocation();
  if (pathname.startsWith('/app/admin/surveys')) return 'Surveys';
  const match = nav
    .filter((n) => !n.end)
    .find((n) => pathname === n.to || pathname.startsWith(n.to + '/'));
  if (match) return match.label;
  if (pathname === '/app/admin' || pathname === '/app/admin/') return 'Overview';
  return 'Admin';
}

export function AdminLayout() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const guard = useAdminGuard();
  const isPlatformAdmin = usePlatformAdmin();
  const [mobileOpen, setMobileOpen] = useState(false);
  const sectionLabel = useAdminSectionLabel();

  if (guard.status === 'not_admin') return <Navigate to="/app/employee" replace />;
  if (guard.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (guard.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-sm text-destructive">{guard.message}</p>
      </div>
    );
  }

  const NavItems = ({ onNavigate }: { onNavigate?: () => void }) => (
    <>
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
              isActive
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
      {isPlatformAdmin === true && platformOnlyNav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
              isActive
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
      {isPlatformAdmin === true && (
        <>
          <div className="mt-4 mb-1 border-t border-border" />
          <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Platform
          </div>
          <NavLink
            to="/app/platform/tenants"
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )
            }
          >
            <Building2 className="h-4 w-4" />
            Tenants
          </NavLink>
        </>
      )}
    </>
  );

  const SignOutButton = ({ onClick }: { onClick?: () => void }) => (
    <Button
      variant="ghost"
      size="sm"
      className="w-full justify-start text-muted-foreground"
      onClick={async () => {
        onClick?.();
        await signOut();
        navigate('/auth/signin');
      }}
    >
      <LogOut className="h-4 w-4 mr-2" />
      Sign out
    </Button>
  );

  return (
    <div className="min-h-screen flex flex-col w-full bg-background">
      <AdminSessionBanner />
      <div className="flex flex-1 w-full min-h-0">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r border-border bg-card">
        <div className="px-5 py-6 border-b border-border">
          <h1 className="text-lg font-heading font-semibold">CultureLens</h1>
          <p className="text-xs text-muted-foreground">Admin console</p>
        </div>
        <RoleSwitcher current="admin" />
        <nav className="flex-1 p-3 space-y-1">
          <NavItems />
        </nav>
        <div className="p-3 border-t border-border">
          <SignOutButton />
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-card border-b border-border flex items-center px-3 gap-3">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open navigation">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0 bg-card flex flex-col">
            <VisuallyHidden asChild>
              <SheetTitle>Admin navigation</SheetTitle>
            </VisuallyHidden>
            <div className="px-5 py-6 border-b border-border">
              <h1 className="text-lg font-heading font-semibold">CultureLens</h1>
              <p className="text-xs text-muted-foreground">Admin console</p>
            </div>
            <RoleSwitcher current="admin" />
            <nav className="flex-1 p-3 space-y-1">
              <NavItems onNavigate={() => setMobileOpen(false)} />
            </nav>
            <div className="p-3 border-t border-border">
              <SignOutButton onClick={() => setMobileOpen(false)} />
            </div>
          </SheetContent>
        </Sheet>
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-sm font-heading font-semibold">Admin</span>
          <span className="text-xs text-muted-foreground truncate">· {sectionLabel}</span>
        </div>
      </div>

      <main className="flex-1 overflow-auto p-6 pt-20 md:pt-6">
        <Outlet context={{ orgId: guard.orgId }} />
      </main>
      </div>
    </div>
  );
}
